import React, { useCallback, useEffect, useState } from "react";
import { Modal, message } from "antd";
import { Redirect, useParams } from "react-router";
import { MDBBox, MDBBtn, MDBRow } from "mdbreact";
import { useSelector } from "react-redux";

import ProjectItemMainAdmin from "components/ProjectItemMainAdmin";
import AddprojectAdmin from "./AddprojectAdmin";
import MemberModalAdmin from "./MemberModalAdmin";
import { mainAdminGetCompanyDetails } from "api/users/mainAdminGetCompanyDetails";
import TransferProjectDialog from "./TransferProjectDialog";
import { undoableTransfers, undoTransfer } from "api/tenantTransfer";

const MainAdminProjects = (props) => {
  const { idCompany } = useParams();

  // The main /admin-panel call is now lightweight — it no longer
  // returns nested projects/timelines/sessions. We fetch the heavy
  // per-company drill-in payload lazily on mount via
  // GET /admin-panel/companies/:companyId. The shape mirrors what
  // s.mainAdmin.company_projects used to provide (one entry per
  // company with .projects/.timelines/.sessions nested) so the rest
  // of this component is unchanged.
  const [company, setCompany] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [fetchState, setFetchState] = useState("loading"); // 'loading'|'ready'|'error'

  // Companies list is still in redux from the lightweight call —
  // we read it so the page can still redirect to /main-admin when
  // the user navigated here directly without a prior list load.
  const companiesListedRedux = useSelector((s) => s.mainAdmin.company);

  const [modal, setModal] = useState("");
  const [parents, setParents] = useState([]);
  const [projectListToEdit, setProjectListToEdit] = useState(null);
  // THE PROJECT BEING HANDED TO ANOTHER TENANT, if any. Super admin only -
  // this whole page is behind that check already, and the server enforces it
  // again on every call rather than trusting the route.
  const [transferring, setTransferring] = useState(null);
  // TRANSFERS INTO THIS COMPANY THAT CAN STILL BE PUT BACK (2026-10-15), by
  // project id. Asked of the server rather than read off
  // project.transferredFrom: a project that was transferred, undone and
  // transferred elsewhere still carries the stamp, but only the latest
  // record that has not been undone can be.
  const [undoable, setUndoable] = useState({});
  const loadUndoable = useCallback(() => {
    undoableTransfers(idCompany)
      .then((r) => {
        if (r && r.err) throw r;
        setUndoable(Object.fromEntries(((r && r.transfers) || []).map((t) => [t.projectId, t])));
      })
      .catch((e) => console.log("undoableTransfers failed:", (e && (e.err || e.message)) || e));
  }, [idCompany]);
  useEffect(() => { loadUndoable(); }, [loadUndoable]);

  useEffect(() => {
    let cancelled = false;
    setFetchState("loading");
    mainAdminGetCompanyDetails(idCompany)
      .then((data) => {
        if (cancelled) return;
        // Backend response: { company: {...}, projects: [...], users: [...] }
        // Wrap into the legacy company_projects shape so the rest of
        // this file's logic (company.projects.filter(...) etc.) just
        // works.
        setCompany({
          ...(data?.company ?? {}),
          projects: data?.projects ?? [],
        });
        setUsersList(data?.users ?? []);
        setFetchState("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        console.log("mainAdminGetCompanyDetails failed:", err?.message || err);
        setFetchState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [idCompany]);

  useEffect(() => {
    setParents(company?.projects?.filter((item) => item.isParent == true));
  }, [company?.projects]);

  const reload = () => {
    mainAdminGetCompanyDetails(idCompany)
      .then((data) => {
        setCompany({ ...(data?.company ?? {}), projects: data?.projects ?? [] });
        setUsersList(data?.users ?? []);
      })
      .catch(() => { /* the dialog already said what happened */ });
    loadUndoable();
  };

  // UNDO NAMES WHERE THE PROJECT RETURNS TO, and sends that company - the
  // original owner - to the API (its old `toCompanyId` meant "back to").
  const askUndo = (project, t) => {
    Modal.confirm({
      title: `Undo transfer - return to ${t.backTo.name}`,
      content: `${project.title} goes back to ${t.backTo.name} with its previous owner. Anything the transfer added for it - the oversight entry it created, ${t.backTo.name}'s partner row, copied lists - is taken back out.`,
      okText: `Return to ${t.backTo.name}`,
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const out = await undoTransfer(project._id, t.backTo._id);
          message.success(`${project.title} is back with ${t.backTo.name}${out && out.removed && out.removed.partnerRow && out.removed.partnerRow !== "none" ? ` (partner row ${out.removed.partnerRow})` : ""}`);
          reload();
        } catch (e) {
          message.error((e && (e.err || e.message)) || "The undo did not run");
        }
      },
    });
  };

  if (fetchState === "error" || (fetchState === "ready" && !company)) {
    return <Redirect to="/main-admin" />;
  }
  if (fetchState === "loading" || !company) {
    return (
      <div className="custom-container">
        <div className="pt-2 w-100" style={{ textAlign: "center", padding: 48 }}>
          Loading company data…
        </div>
      </div>
    );
  }
  // Drop-in local for code below that previously read this name.
  const usersRedux = usersList;
  const companyUser = company?.users;

  return (
    <div className="custom-container">
      <MDBBox className="pt-2 w-100">
        <MDBBox className="d-flex justify-content-between align-items-center">
          <h2>{company?.companyName} projects</h2>
          <div
            onClick={() => setModal("CreateModal")}
            data-toggle="modal"
            data-target="#exampleModalCenter"
            className="header-btn bg-bid text-white border-bid rounded-lg mr-0 mb-0 cursor-pointer f-14 roboto font-weight-normal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
            >
              <path
                d="M5.60013 9.11418H6.61313V6.62315H9.11313V5.61015H6.61313V3.03819H5.60013V5.61015H3.03813V6.62315H5.60013V9.11418ZM6.07913 12.1521C5.26654 12.1572 4.46167 11.9941 3.71513 11.6731C2.99435 11.3666 2.33864 10.9254 1.78313 10.3732C1.23087 9.81769 0.78965 9.16196 0.483133 8.44114C0.160192 7.69298 -0.00426501 6.88603 0.000131185 6.07115C-0.00529131 5.25797 0.157444 4.45248 0.478128 3.70518C0.783938 2.98546 1.22529 2.33125 1.77813 1.77818C2.33424 1.22671 2.9898 0.785612 3.71013 0.478193C4.45897 0.156833 5.26627 -0.00594709 6.08113 0.000166009C6.89431 -0.00525648 7.69983 0.157509 8.44713 0.478193C9.16637 0.784898 9.82042 1.22612 10.3741 1.77818C10.9265 2.33241 11.3678 2.98713 11.6741 3.70714C11.9947 4.45482 12.1574 5.26065 12.1521 6.07414C12.1575 6.88667 11.9948 7.69154 11.6741 8.43815C11.3669 9.15798 10.9257 9.81291 10.3741 10.3681C9.81993 10.9206 9.16516 11.3619 8.44513 11.6682C7.69798 11.991 6.89203 12.1554 6.07813 12.1512L6.07913 12.1521Z"
                fill="#fff"
              />
            </svg>{" "}
            <span className="ml-2 font-weight-normal">Add Projects</span>
          </div>
        </MDBBox>
        <MDBRow className="row-cols-lg-4 row-cols-sm-3 row-cols-1 mt-3">
          {company.projects.map((project, index) => {
            const userWhoCreateTest = usersRedux.find(
              (user) => project.user === user._id
            );
            return (
              <ProjectItemMainAdmin
                key={project._id}
                projectItem={project}
                className={index > 2 ? "mt-3" : ""}
                userWhoCreate={userWhoCreateTest}
                openMembersModal={() => {
                  setProjectListToEdit(project);
                  setModal("ProjectListToEdit");
                }}
                parentProjectID={project?.parentProjectID}
                isParent={project?.isParent}
                onTransfer={() => setTransferring(project)}
                transferUndo={undoable[String(project._id)] || null}
                onUndoTransfer={(t) => askUndo(project, t)}
              />
            );
          })}
        </MDBRow>
 
        {transferring ? (
          <TransferProjectDialog
            project={transferring}
            companies={companiesListedRedux || []}
            fromCompanyId={idCompany}
            onClose={() => setTransferring(null)}
            onDone={() => {
              // The project has left this company (or come back to it, after
              // the dialog's own Undo), so the list it was in is no longer
              // the truth. Re-read rather than splice it out of local state -
              // a transfer changes the owner too.
              reload();
            }}
          />
        ) : null}

        <AddprojectAdmin
          parents={parents}
          isOpen={modal === "CreateModal"}
          toggle={() => setModal("")}
          data={company}
        />

        <MemberModalAdmin
          open={modal === "ProjectListToEdit"}
          toggle={() => setModal("")}
          projectListToEdit={projectListToEdit ? projectListToEdit._id : null}
          companyUser={companyUser}
          companyProjects={company}
        />
      </MDBBox>
    </div>
  );
};

export default MainAdminProjects;
